import React, { ReactNode, useEffect, useState } from 'react';
import { FiscalConfigProvider } from '@/contexts/FiscalConfigContext';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

interface FiscalConfigWrapperProps {
  children: ReactNode;
}

export const FiscalConfigWrapper: React.FC<FiscalConfigWrapperProps> = ({ children }) => {
  const { user, selectedCompany } = useAuth();
  const [currentBranchId, setCurrentBranchId] = useState<string>('');
  const [branchLoaded, setBranchLoaded] = useState(false);

  useEffect(() => {
    const loadUserBranch = async () => {
      if (!user || !selectedCompany?.id) {
        setBranchLoaded(true);
        return;
      }

      try {
        // 1) Priorizar la sucursal de la sesión de caja abierta en la empresa actual
        const { data: sessions, error: sessionError } = await supabase
          .from('cash_register_sessions')
          .select('branch_id')
          .eq('user_id', user.id)
          .eq('company_id', selectedCompany.id)
          .eq('status', 'open')
          .order('opened_at', { ascending: false })
          .limit(1);

        if (!sessionError && sessions && sessions.length > 0 && sessions[0].branch_id) {
          setCurrentBranchId(sessions[0].branch_id);
          setBranchLoaded(true);
          return;
        }

        // 2) Fallback: sucursal por defecto del usuario dentro de la empresa actual
        const { data: userBranches, error } = await supabase
          .from('user_branches')
          .select(`
            branch_id, 
            is_default,
            branch:branches!fk_user_branches_branch_id(id, name, code, company_id)
          `)
          .eq('user_id', user.id)
          .eq('branch.company_id', selectedCompany.id)
          .order('is_default', { ascending: false })
          .limit(1);

        if (!error && userBranches && userBranches.length > 0) {
          const branchId = userBranches[0].branch_id;
          setCurrentBranchId(branchId);
        } else {
          setCurrentBranchId('');
        }
      } catch (error) {
        setCurrentBranchId('');
      } finally {
        setBranchLoaded(true);
      }
    };

    setBranchLoaded(false);
    loadUserBranch();
  }, [user, selectedCompany?.id]);

  // No renderizar el provider hasta que se haya cargado la información de la sucursal
  if (!branchLoaded) {
    return null;
  }

  return (
    <FiscalConfigProvider 
      companyId={selectedCompany?.id} 
      branchId={currentBranchId || undefined}
    >
      {children}
    </FiscalConfigProvider>
  );
};